import { useMemo } from 'react';

import useCollectionTags from './useCollectionTags';
import { useOptionalTags } from '../contexts/TagsContext';
import { tagsFilterField } from '../utils/kqlBuilder';

const NONE = Object.freeze([]);

/**
 * THE `tags` FIELD WHERE A FILTER DECIDES WHO SEES WHAT (T7; docs/plans/
 * tags.md §3.5; keepr-api § Querying tags, the `access` mode): an access
 * level's filter and a dynamic group's rule (the Sharing tab), a collection
 * link's filter (LinkShape) and a public link's (ShareLinksPanel). The
 * builders add it the way the items page adds its own `tagsField`.
 *
 * The collection's vocabulary and its parents' (useCollectionTags — one
 * shared, session-stamped read) rides on the field whole, so the value check
 * can say why an unrestricted tag can't be used; the picker offers the
 * RESTRICTED ones alone (components/common/FilterBuilder), and says what to
 * do when there are none. My private tags ride along only so a typed one is
 * recognised and refused; they are never offered.
 *
 * `useAccessTagsField(collectionId, { enabled })` → the field, or null with
 * no collection.
 */
export default function useAccessTagsField(collectionId, { enabled = true } = {}) {
	const on = Boolean(enabled && collectionId);
	const { tags, loaded } = useCollectionTags(collectionId, { enabled: on });
	const privateTags = useOptionalTags()?.tags || NONE;
	return useMemo(() => (on ? tagsFilterField({
		tags,
		privateTags,
		mode: 'access',
		loaded,
	}) : null), [on, tags, privateTags, loaded]);
}
